import { Card, InputRef, Typography } from "antd";
import { useEffect, useRef, useState } from "react";
import { openNotification } from "../../Components/Notification";
import useOrganization from "../../Hooks/useOrganization";
import jobDescriptionService from "../../Service/job.description.service";
import JobShareDrawer from "../../Components/JobShareDrawer";
import JobShareSection from "../../Components/JobShareSection";
import JobPostsStatusGraph from "./JobPostsStatusGraph";
import { CHART_COLORS } from "../JobPostDetails/components/StatsAndAnalytics/utils/chartColors";

const CareersSection = () => {
  const [jobPosts, setJobPosts] = useState<any>([]);
  const { org } = useOrganization();
  const inputRef = useRef<InputRef>(null);
  const [isShareDrawerOpen, setIsShareDrawerOpen] = useState(false);
  const [selectedPlatform, setSelectedPlatform] = useState<string>("");
  const [hasGraphs, setHasGraphs] = useState(false);

  const navigateTo = () => {
    window.open(`https://${org?.orgDomain}`, "_blank");
  };

  const handlePlatformClick = (platform: string) => {
    setSelectedPlatform(platform);
    setIsShareDrawerOpen(true);
  };

  const handleShareDrawerClose = () => {
    setIsShareDrawerOpen(false);
    setSelectedPlatform("");
  };

  useEffect(() => {
    const fetchJobPosts = async () => {
      try {
        const data = await jobDescriptionService.getAllJobDescriptions(1);
        setJobPosts(data?.data || []);
      } catch (error) {
        console.error("CareersSection: Fetch failed", error);
        openNotification("error", "Fetch failed", "Unable to fetch job posts.");
      }
    };

    fetchJobPosts();
  }, []);

  const fallbackCopyToClipboard = (text: string) => {
    const textArea = document.createElement("textarea");
    textArea.value = text;
    textArea.style.position = "fixed";
    textArea.style.top = "0";
    textArea.style.left = "0";
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      document.execCommand("copy");
      openNotification(
        "success",
        "Copied",
        "The URL has been copied to your clipboard."
      );
    } catch (err) {
      openNotification("error", "Copy failed", "Unable to copy the URL.");
      console.error("CareersSection: Error copying URL:", err);
    }

    document.body.removeChild(textArea);
  };

  const handleCopy = async () => {
    if (org?.orgDomain) {
      // Check if Clipboard API is supported
      if (navigator.clipboard) {
        try {
          await navigator.clipboard.writeText(`https://${org?.orgDomain}`);
          openNotification(
            "success",
            "Copied",
            "The careers site URL has been copied to your clipboard."
          );
        } catch (err) {
          openNotification("error", "Copy failed", "Unable to copy the URL.");
          console.error("Failed to copy: ", err);
        }
      } else {
        // Fallback to execCommand in case Clipboard API is not supported
        fallbackCopyToClipboard(org.orgDomain);
      }
    }
  };

  const hasActiveJobPost = jobPosts.some((post: any) => post.isActive === true);

  return (
    <div className="careers-cards-wrapper">
      <Card
        className={`job-share-section-card`}
      >
        <JobShareSection
          jobLink={`https://${org?.orgDomain}`}
          jobTitle="Careers Page"
          hasActiveJobPost={hasActiveJobPost}
          inputRef={inputRef}
          onNavigateTo={navigateTo}
          onCopy={handleCopy}
          onPlatformClick={handlePlatformClick}
        />

        <JobShareDrawer
          isOpen={isShareDrawerOpen}
          onClose={handleShareDrawerClose}
          jobTitle="Careers Page"
          jobLink={`https://${org?.orgDomain}`}
          selectedPlatform={selectedPlatform}
        />
      </Card>
      <Card className="job-status-card">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <Typography.Title level={5} style={{ margin: 0 }}>
            Application status
          </Typography.Title>
          {hasGraphs && (
            <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span 
                  style={{ 
                    width: 10, 
                    height: 10, 
                    borderRadius: 2, 
                    backgroundColor: CHART_COLORS.success,
                    display: "inline-block"
                  }} 
                />
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>Qualified</Typography.Text>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span 
                  style={{ 
                    width: 10, 
                    height: 10, 
                    borderRadius: 2, 
                    backgroundColor: "#d9d9d9",
                    display: "inline-block"
                  }} 
                />
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>Unqualified</Typography.Text>
              </div>
            </div>
          )}
        </div>
        <JobPostsStatusGraph onHasGraphsChange={setHasGraphs} />
      </Card>
    </div>
  );
};

export default CareersSection;
